import Component from '@glimmer/component'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import type FeaturesService from 'teacher-ui-client/services/features'
import type DashboardPreferencesService from 'teacher-ui-client/services/dashboard-preferences'
import type RouterService from '@ember/routing/router-service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import { teacherViewedEvent } from 'teacher-ui-client/modifiers/teacher-viewed-event'
import { hash } from '@ember/helper'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import insightsPreview from '@images/teacher-home-banner/insights-preview.png'

export class TeacherHomeBanner extends Component {
  @service features!: FeaturesService
  @service('dashboard-preferences') dashboardPreferences!: DashboardPreferencesService
  @service router!: RouterService
  @service('subscription-type') subscriptionType!: SubscriptionTypeService
  @service teacherEvents!: TeacherEvents

  @tracked isSaving = false

  get isVisible() {
    return this.features.isEnabled('tui_teacher_home') && !this.dashboardPreferences.hasPersistedTeacherHomePreference
  }

  get product() {
    return String(this.subscriptionType.current)
  }

  dismiss = async () => {
    if (this.isSaving) return

    this.isSaving = true

    this.teacherEvents.sendEvent('TeacherHome:banner_dismissed', this.product)

    try {
      await this.dashboardPreferences.setTeacherHomeEnabled(false)
    } finally {
      this.isSaving = false
    }
  }

  enableTeacherHome = async () => {
    if (this.isSaving) return

    this.isSaving = true

    try {
      await this.dashboardPreferences.setTeacherHomeEnabled(true)
      this.teacherEvents.sendEvent('TeacherHome:opted_in', this.product)
      void this.router.transitionTo('subscription-type.teacher-home', this.subscriptionType.current)
    } finally {
      this.isSaving = false
    }
  }

  <template>
    {{#if this.isVisible}}
      <div
        class="to-rexy-purple-50/50 from-3E-pink-50 relative mb-10 w-full rounded-2xl bg-linear-to-r p-6 pr-12"
        {{teacherViewedEvent "TeacherHome:banner_viewed" this.product eventData=(hash)}}
        data-test-teacher-home-banner
      >

        <div class="flex items-center justify-between gap-10 xl:gap-16">
          <div class="space-y-12">
            <div class="space-y-1">
              <h2 class="text-xl font-semibold text-neutral-300">{{t "teacherHome.banner.title"}}</h2>
              <p class="text-lg text-neutral-300">{{t "teacherHome.banner.description"}}</p>
            </div>

            <button
              type="button"
              class="bg-3E-pink-300 hover:bg-pink-350 focus:bg-pink-350 inline-flex min-w-48 justify-center rounded-lg px-4 py-2 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
              {{on "click" this.enableTeacherHome}}
              disabled={{this.isSaving}}
              data-test-teacher-home-banner-cta
            >
              {{t "teacherHome.banner.ctaLabel"}}
            </button>
          </div>
          <div class="relative flex shrink-0 basis-1/3 items-center justify-center overflow-visible">
            <img
              src={{insightsPreview}}
              alt={{t "teacherHome.banner.imageAlt"}}
              class="absolute h-56 rotate-6 rounded-lg object-cover shadow-md"
            />

          </div>

        </div>
        <button
          type="button"
          aria-label={{t "close"}}
          class="text-neutral-250 hover:text-neutral-350 absolute top-2 right-2 inline-flex size-8 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-white"
          {{on "click" this.dismiss}}
          disabled={{this.isSaving}}
          data-test-teacher-home-banner-dismiss
        >
          <PhosphorIcon @icon="phosphor-x-bold" class="size-4" />
        </button>
      </div>
    {{/if}}
  </template>
}

export default TeacherHomeBanner
